import { IngredientsContext } from "@/app/clientLayout";
import { SortableColumn } from "@/components/DragNDrop";
import { UserContext } from "@/components/Page";
import Tooltip from "@/components/styles/Tooltip";
import DragIndicatorIcon from "@/components/svg/DragIndicatorIcon";
import useDebounce from "@/lib/useDebounce";
import {
  CheckCircleIcon,
  PlusIcon,
  TrashIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import React, { useContext, useEffect, useState } from "react";
import { textToIngredient } from "../util";
import { PartialRecipe } from "./FinalisePartialRecipe";
import UploadedRecipeIngredient from "./UploadedRecipeIngredient";

interface Props {
  uuid: string;
  ris: PartialRecipe["ingredientSections"][number];
  risIndex: number;
  updateSectionName: (id: string, suuid: string, newName: string) => void;
  addSectionIngredient: (
    id: string,
    suuid: string,
    newIngredient: PartialRecipe["ingredientSections"][number]["ingredients"][number]
  ) => void;
  removeSection: (id: string, suuid: string) => void;
  setIngredient: (
    id: string,
    suuid: string,
    sub: string,
    newSelected: PartialRecipe["ingredientSections"][number]["ingredients"][number]["selected"]
  ) => void;
  setAmount: (
    id: string,
    suuid: string,
    sub: string,
    value: string | number
  ) => void;
  setPreparation: (
    id: string,
    suuid: string,
    sub: string,
    value: string
  ) => void;
  setUnitId: (id: string, suuid: string, sub: string, value: string) => void;
  removeIngredient: (id: string, suuid: string, sub: string) => void;
}

export const IngredientSection: React.FC<Props> = ({
  uuid,
  ris,
  risIndex,
  updateSectionName,
  addSectionIngredient,
  removeSection,
  setIngredient,
  setAmount,
  setPreparation,
  setUnitId,
  removeIngredient,
}) => {
  const { ingredients } = useContext(IngredientsContext);
  const user = useContext(UserContext);

  const [isAddingIngredient, setAddingIngredient] = useState(false);

  // For a faster UI response, we use temp variables
  // and debounce the actual update
  const [tempSectionName, setTempSectionName] = useState(ris.name);

  const debouncedSectionName = useDebounce(tempSectionName, 1000);

  useEffect(() => {
    if (ris.name !== debouncedSectionName) {
      console.log("section name changed");
      updateSectionName(uuid, ris.uuid, debouncedSectionName);
    }
  }, [debouncedSectionName]);

  useEffect(() => {
    console.log("Mounted IngredientSection");
    return () => console.log("Unmounted IngredientSection");
  }, []);

  return (
    <SortableColumn id={ris.uuid} index={risIndex}>
      {({ ref, handleRef, isDropTarget }) => (
        <div ref={ref}>
          <div className="group/section rounded-lg bg-slate-200 px-2.5 pb-4">
            {ris.section ? (
              <div className="group -ml-3 flex items-center gap-2.5 py-2.5">
                <div
                  className="flex items-center justify-center"
                  title="Sort Sections"
                  ref={handleRef}
                >
                  <DragIndicatorIcon className="h-6" />
                </div>
                <input
                  type="text"
                  value={tempSectionName}
                  onChange={(e) => setTempSectionName(e.target.value)}
                  onFocus={(e) => e.target.select()}
                  className="rounded border border-solid border-gray-300 p-1.5"
                  autoFocus
                />
                <hr className="flex-1 border-gray-400" />
                <Tooltip content="Remove Section">
                  <button
                    onClick={() => removeSection(uuid, ris.uuid)}
                    className="opacity-0 transition group-hover/section:opacity-100"
                  >
                    <TrashIcon className="icon icon-orange" />
                  </button>
                </Tooltip>
              </div>
            ) : (
              <div className="group -ml-3 flex items-center gap-2.5 py-2.5">
                <div
                  className="flex items-center justify-center"
                  title="Sort Ingredients"
                  ref={handleRef}
                >
                  <DragIndicatorIcon className="h-6" />
                </div>
                <h3 className="font-medium text-gray-500/30">
                  Default Section
                </h3>
              </div>
            )}
            <div className="space-y-2.5">
              {ris.ingredients.length ? (
                <>
                  {ris.ingredients.map((i, index) => (
                    <UploadedRecipeIngredient
                      key={i.uuid}
                      index={index}
                      uuid={uuid}
                      suuid={ris.uuid}
                      ingredient={i}
                      setIngredient={setIngredient}
                      setAmount={setAmount}
                      setPreparation={setPreparation}
                      setUnitId={setUnitId}
                      removeIngredient={removeIngredient}
                    />
                  ))}
                </>
              ) : (
                <>
                  {!tempSectionName || isDropTarget ? null : (
                    <div className="rounded border border-dashed border-gray-300 bg-gradient-to-tr from-cyan-50 to-gray-50 p-2 text-center">
                      <h3>Empty Section</h3>
                      <small>
                        Add ingredients to this section by dragging them.
                      </small>
                    </div>
                  )}
                </>
              )}
            </div>

            {isAddingIngredient ? (
              <form
                onSubmit={async (e) => {
                  e.preventDefault();

                  const target = e.target as typeof e.target & {
                    newIngredientText: HTMLInputElement;
                  };

                  const ingredient = await textToIngredient(
                    user,
                    ingredients,
                    target.newIngredientText.value
                  );
                  addSectionIngredient(uuid, ris.uuid, ingredient);

                  setAddingIngredient(false);
                }}
                className="mt-2 flex items-end justify-center"
              >
                <label htmlFor="newIngredientText" className="flex-grow">
                  Text
                  <input
                    type="text"
                    name="newIngredientText"
                    id="newIngredientText"
                    required
                    placeholder="5tsp sugar"
                    autoFocus
                    className="input w-full"
                  />
                </label>
                <button type="submit" className="btn btn-primary mb-1">
                  <CheckCircleIcon className="w-6 text-green-800" />
                </button>
                <button
                  type="button"
                  className="btn btn-warning mb-1"
                  onClick={() => setAddingIngredient(false)}
                >
                  <XCircleIcon className="w-6 text-orange-900" />
                </button>
              </form>
            ) : (
              <div className="mt-4 flex items-center justify-center">
                <button
                  className="btn btn-sm flex items-center gap-1.5 text-base font-medium"
                  onClick={() => setAddingIngredient(true)}
                >
                  <PlusIcon className="w-5" /> Add an Ingredient
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </SortableColumn>
  );
};
